import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { filter, map, isEmpty, omitBy, isNil, isNull } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { decode, encode, toaster } from "Helpers/index";
import useSession from "Hooks/session.hook";

import { DataTable, SelectInput } from "@life-line-lab/components-react";
import { MoreOutlined } from "@ant-design/icons";
import AssignDriverBulkLooseModal from "../../parcel-tracking-modals/assign-driver-bulk-loose-modal/assign-driver-bulk-loose";

import SetHandoverPointModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/set-handover-point";

// SHARED COMPONENT
import DataTableFilter from "Components/shared/data-table-filter";

import { ActionMenu, Button, BasicModal } from "@life-line-lab/components-react";
import { routeToOrderView } from "Helpers/route-order-view";

export interface Option {
  label: string;
  value: string;
}

interface SortingBulkListProps {
  onFilter?: any;
  customerOptions?: any;
  sortingHubOptions?: any;
  allOrderList?: any;
  vehicleOptions?: any;
  onAssignDriver?: any;
  //TODO: Modify these optional / required after getting the actual data

  onSetHandoverPoint: any;
}

const SortingBulkList: React.FC<SortingBulkListProps> = ({
  customerOptions,
  sortingHubOptions,
  allOrderList,
  vehicleOptions,
  onFilter,
  onAssignDriver,
  onSetHandoverPoint,
}) => {
  const [callModal, setCallModal] = useState<any>(<></>);
  const { org, roles } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const initialModal: any = {
    open: false,
  };

  const [modal, setModal] = useState<any>(initialModal);

  const [selectedRows, setSelectedRows] = useState<any>([]);
  const { roleTypes } = useSession();

  // modal is not seen initially
  const handleResetModalState = () => {
    setModal(initialModal);
  };

  // modal to set drivers
  const handleAssignDriverModal = (orderID?: string, assignType?: string) => {
    setCallModal(
      <AssignDriverBulkLooseModal
        vehicleOptions={vehicleOptions}
        selectedOrder={orderID}
        type={assignType || "ASSIGN"}
        handleCancel={handleResetModalState}
        onSubmit={(data) => {
          onAssignDriver(data);
          handleResetModalState();
        }}
      />,
    );
    setModal({ open: true });
  };

  // to see parcel order
  const handleOrderView = (orderID) => {
    const params = 'module=delivery&tabKey=2&subTab=1'
    routeToOrderView(orderID, location, history, params)
  };

  // filter begins empty showing all lists
  const initialFilter: any = {
    customersList: "",
    sortingHub: "",
  };

  const [filterState, setFilterState] = useState<{ [key: string]: string | boolean }>(
    initialFilter,
  );

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input) || isEmpty(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  // all filter options
  const initialFilterSelection = [
    {
      key: "customer",
      name: "customersList",
      label: t("placeholder.filterCustomer"),
      placeholder: t("placeholder.filterCustomer"),
      value: filterState?.customersList,
      options: [{ label: t("option.all"), value: "" }].concat(
        customerOptions.map((org) => {
          return {
            label: org.name || "",
            value: org._id,
          };
        }),
      ),
    },
    {
      key: "sortingHub",
      name: "sortingHub",
      label: t("placeholder.byOriginHub"),
      placeholder: t("placeholder.byOriginHub"),
      value: filterState?.sortingHub,
      options: [{ label: t("option.all"), value: "" }].concat(
        sortingHubOptions?.map((hub) => {
          return {
            label: hub.locationName || "",
            value: hub._id,
          };
        }),
      ),
    },
  ];

  const [filterSelection, setFilterSelection] = useState<any>(initialFilterSelection);

  // search string
  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      customersList: selection.customersList,
      vehicleType: selection.vehicleType,
      driver: selection.driver,
      sortingHub: selection.sortingHub,
    }));
  };

  // table setup
  const columns: any = [
    {
      title: t("label.originHub").toLocaleUpperCase(),
      dataIndex: "midMileInfoData",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end ">
            <span
              className="half-square custom-icon mb-5"
              style={{ borderLeft: `12px solid transparent`, borderBottom: `12px solid #E8A51A` }}
            >
              &nbsp;
            </span>
            <span>
              &nbsp;
              {record.midMileInfoData ? record.midMileInfoData?.sortingHub?.name : "Missing Info"}
            </span>
          </div>
        );
      },
    },
    {
      title: t("label.parcelOrderNo").toLocaleUpperCase(),
      dataIndex: "orderCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.orderCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOrderView(record._id);
          },
        };
      },
    },
    {
      title: t("label.parcelTrackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNumber",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: ["senderID", "name"],
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.noOfParcel").toLocaleUpperCase(),
      dataIndex: "parcels",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.parcels?.length}</span>;
      },
    },
    {
      title: t("label.weightInKg").toLocaleUpperCase(),
      dataIndex: "weight",
      width: 105,
      ellipsis: true,
      render: (weight: string) => {
        const roundedWeight = parseFloat(weight).toFixed(2);
        return <span>{roundedWeight}</span>;
      },
    },
    {
      title: t("label.cubicVolume").toLocaleUpperCase(),
      dataIndex: "volume",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.volume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.handoverPoint").toLocaleUpperCase(),
      dataIndex: ["midMileInfoData", "handoverPoint", "name"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            &nbsp;
            {record.midMileInfoData ? record.midMileInfoData.handoverPoint.name : "Missing Info"}
          </span>
        );
      },
    },
    {
      title: t("label.driverName").toLocaleUpperCase(),
      dataIndex: ["driverData"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {!isNull(record.assignedDriverData)
              ? record.assignedDriverData?.fullName
              : record.assignedDriver || "Unassigned"}
          </span>
        );
      },
    },
  ];

  const companyColumns = columns.concat({
    title: "",
    dataIndex: [],
    width: 80,
    render: (_: any, record: any, rowIndex: string) => (
      <ActionMenu
        label={<MoreOutlined />}
        actions={[
          {
            label: t("action.assignDriver"),
            onClick: () => {
              handleAssignDriverModal(record._id, record.assignedDriver ? "REASSIGN" : "ASSIGN");
            },
            isDisable: false,
          },
        ]}
      />
    ),
  });

  // handle all handover point submission and close the modal
  const handleSetHandoverPoint = (payload) => {
    onSetHandoverPoint(payload);
    handleResetModalState();
  };

  // feat: do the same for sorting-loose-list
  const handleOpenSetHandover = () => {
    let orderBatches: any = [];

    // if orders are selected, add it into orderBatches: []
    allOrderList.map((order) => {
      selectedRows.map((selected) => {
        if (order._id === selected) {
          orderBatches.push(order);
        }
      });
    });

    // once completed, call the modal to prepare handover selection
    // selectedOptions - take filtered data: "customer type", "handover point", "origin hub"
    // handoverOptions -
    setCallModal(
      <SetHandoverPointModal
        orders={orderBatches}
        handoverOptions={sortingHubOptions}
        handleCancel={handleResetModalState}
        onSubmit={handleSetHandoverPoint}
        setSelectedRows={setSelectedRows}
      />,
    );
    setModal({ open: true, type: "CREATE" });
  };

  const rowSelection =
    roleTypes !== "CUSTOMER-SUPER_ADMIN"
      ? {
          selectedRowKeys: selectedRows,
          // disable checkbox if there is a handover point
          // -> in the future, remove this to enable overriding current handover, but as of now, not allowed
          getCheckboxProps: (record) => {
            return {
              disabled: record?.midMileInfo?.handoverPoint, // disable when handoverPoint exists
            };
          },
          onChange: (newSelectedRows: any[]) => {
            // store an array of orders that are checked
            // selected data is designated by DataTable's rowKey prop
            setSelectedRows(newSelectedRows);
          },
        }
      : undefined;

  return (
    <>
      <div className="parcel-order-list">
        <DataTable
          rowKey={(record: any) => {
            return record._id;
          }}
          scroll={{ x: true }}
          rowSelection={rowSelection}
          columns={roles[0].type == "COMPANY" ? companyColumns : columns}
          dataSource={allOrderList || []}
          title={() => (
            <div>
              <div className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.sortingSearchbar")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
                {roleTypes == "COMPANY-SUPER_ADMIN" && (
                  <div className="mt-10">
                    <Button
                      className="mr-5"
                      type="default"
                      isDisabled={isEmpty(selectedRows)}
                      label={t("action.setHandoverPoint")}
                      size="large"
                      onClick={handleOpenSetHandover}
                    />
                  </div>
                )}
              </div>
              <div>
                <h4>Total Bulk: {allOrderList.length}</h4>
              </div>
            </div>
          )}
        />
        <BasicModal
          className="modal"
          isOpen={modal.open}
          isClosable={true}
          isMaskClosable={false}
          onClose={() => {
            handleResetModalState();
          }}
        >
          {/*call the state in the basic modal*/}
          {callModal}
        </BasicModal>
      </div>
    </>
  );
};

export default SortingBulkList;
